import { useState, useEffect, useCallback } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { useAlertCount } from 'components/shared/NotificationPanel';
import api from 'services/api';
import { formatDate, aujourdHui } from '../../../../utils/jour';

const CATEGORIES = [
  'Salaires et charges sociales',
  'Loyer et charges locatives',
  'Carburant vehicules',
  'Achats equipements et materiel',
  'Frais telephone et internet',
  'Entretien et reparations',
  'Frais divers exploitation',
];

const EMPTY_FORM = {
  categorie: '',
  description: '',
  montant: '',
  date: aujourdHui(),
  justificatif_url: '',
};

export default function Charges() {
  const alertCount = useAlertCount();
  usePageTitle('Charges & Depenses', alertCount);

  const [charges, setCharges] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Filters
  const now = new Date();
  const [debut, setDebut] = useState(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10));
  const [fin, setFin] = useState(now.toISOString().slice(0, 10));
  const [filterCat, setFilterCat] = useState('');

  const loadCharges = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (debut) params.debut = debut;
      if (fin) params.fin = fin;
      if (filterCat) params.categorie = filterCat;
      const res = await api.get('/charges', { params });
      setCharges(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [debut, fin, filterCat]);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/charges/stats', { params: { annee: now.getFullYear() } });
      setStats(res.data);
    } catch {
      // silent
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCharges();
    loadStats();
  }, [loadCharges, loadStats]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* ⓘ Le formateur local a ete retire le 16/10/2026 : `c.date` est une colonne
   *   `date`, et `new Date(d)` la reinterpretait en UTC — donc la veille dans un
   *   fuseau negatif. `utils/jour.js#formatDate` decoupe la chaine. */

  const formatMoney = (v) => Number(v || 0).toLocaleString() + ' FCFA';

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const openEdit = (charge) => {
    setEditId(charge.id);
    setForm({
      categorie: charge.categorie || '',
      description: charge.description || '',
      montant: charge.montant || '',
      date: charge.date ? charge.date.slice(0, 10) : '',
      justificatif_url: charge.justificatif_url || '',
    });
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.categorie || !form.montant || !form.date) {
      setError('Categorie, montant et date sont obligatoires.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, montant: parseFloat(form.montant) };
      if (editId) {
        await api.put(`/charges/${editId}`, payload);
      } else {
        await api.post('/charges', payload);
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditId(null);
      loadCharges();
      loadStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette depense ?')) return;
    try {
      await api.delete(`/charges/${id}`);
      loadCharges();
      loadStats();
    } catch {
      // silent
    }
  };

  // Compute category summary from current filtered charges
  const catSummary = charges.reduce((acc, c) => {
    const cat = c.categorie || 'Non categorise';
    acc[cat] = (acc[cat] || 0) + Number(c.montant || 0);
    return acc;
  }, {});
  const catSummaryEntries = Object.entries(catSummary).sort((a, b) => b[1] - a[1]);

  return (
    <div>
{/* Stats cards */}
      {stats && (
        <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Charges mois en cours</span>
              <div className="stat-card-icon blue">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{formatMoney(stats.total_mois)}</div>
            <div className="stat-card-sub">Mois en cours</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Charges trimestre</span>
              <div className="stat-card-icon green">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{formatMoney(stats.total_trimestre)}</div>
            <div className="stat-card-sub">Trimestre en cours</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Charges annee</span>
              <div className="stat-card-icon orange">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
            </div>
            <div className="stat-card-value">{formatMoney(stats.total_annee)}</div>
            <div className="stat-card-sub">Annee {now.getFullYear()}</div>
          </div>
          <div className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-label">Categorie la plus couteuse</span>
              <div className="stat-card-icon red">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              </div>
            </div>
            <div className="stat-card-value" style={{ fontSize: '1rem' }}>{stats.categorie_max || '-'}</div>
            <div className="stat-card-sub">{stats.categorie_max_montant ? formatMoney(stats.categorie_max_montant) : '-'}</div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="content-card">
        <div className="gss-toolbar">
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="date"
              className="gss-input"
              value={debut}
              onChange={e => setDebut(e.target.value)}
              style={{ width: '160px' }}
            />
            <input
              type="date"
              className="gss-input"
              value={fin}
              onChange={e => setFin(e.target.value)}
              style={{ width: '160px' }}
            />
            <select
              className="gss-input"
              value={filterCat}
              onChange={e => setFilterCat(e.target.value)}
              style={{ width: '240px' }}
            >
              <option value="">Toutes les categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button className="gss-btn gss-btn-primary" onClick={openCreate}>+ Nouvelle depense</button>
        </div>

        {/* Table */}
        {loading ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
        ) : charges.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Aucune depense pour cette periode</p>
        ) : (
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Categorie</th>
                  <th>Description</th>
                  <th>Montant</th>
                  <th>Justificatif</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {charges.map(c => (
                  <tr key={c.id}>
                    <td style={{ whiteSpace: 'nowrap', color: 'var(--gss-text-secondary)', fontSize: '0.85rem' }}>{formatDate(c.date)}</td>
                    <td>
                      <span className="gss-badge" style={{ background: 'rgba(37,99,235,0.08)', color: '#2563EB' }}>
                        {c.categorie || '-'}
                      </span>
                    </td>
                    <td style={{ maxWidth: '300px' }}>{c.description || '-'}</td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatMoney(c.montant)}</td>
                    <td>
                      {c.justificatif_url ? (
                        <a href={c.justificatif_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gss-primary)', textDecoration: 'underline' }}>Voir</a>
                      ) : '-'}
                    </td>
                    <td>
                      <div className="gss-actions">
                        <button className="gss-btn-icon" title="Modifier" onClick={() => openEdit(c)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </button>
                        <button className="gss-btn-icon gss-btn-icon--delete" title="Supprimer" onClick={() => handleDelete(c.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Category summary */}
      {catSummaryEntries.length > 0 && (
        <div className="content-card" style={{ marginTop: '1.5rem' }}>
          <h3 className="content-card-title">Repartition par categorie</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {catSummaryEntries.map(([cat, total]) => {
              const maxVal = catSummaryEntries[0][1];
              const pct = maxVal > 0 ? (total / maxVal) * 100 : 0;
              return (
                <div key={cat}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{cat}</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{formatMoney(total)}</span>
                  </div>
                  <div style={{ background: 'rgba(0,0,0,0.04)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--gss-primary, #DC2626)', borderRadius: '4px', transition: 'width 0.3s ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal create/edit */}
      {showModal && (
        <div className="gss-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                <h2>{editId ? 'Modifier la depense' : 'Nouvelle depense'}</h2>
              </div>
              <div className="gss-modal-subtitle">Enregistrement de charge</div>
              <button className="gss-modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            {error && <div className="gss-alert gss-alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 7h16M4 12h16M4 17h10"/>
                </svg>
                <span>CLASSIFICATION</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Categorie *</label>
                  <select className="gss-input" required value={form.categorie} onChange={e => setField('categorie', e.target.value)}>
                    <option value="">-- Selectionner --</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="gss-field gss-field-full">
                  <label>Description</label>
                  <textarea className="gss-input" rows="3" value={form.description} onChange={e => setField('description', e.target.value)} />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                </svg>
                <span>MONTANT & DATE</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Montant (FCFA) *</label>
                  <input className="gss-input" type="number" min="0" step="1" required value={form.montant} onChange={e => setField('montant', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Date *</label>
                  <input className="gss-input" type="date" required value={form.date} onChange={e => setField('date', e.target.value)} />
                </div>
              </div>

              <div className="gss-modal-section">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                <span>JUSTIFICATIF</span>
              </div>
              <div className="gss-form-grid">
                <div className="gss-field">
                  <label>Justificatif (URL)</label>
                  <input className="gss-input" type="text" placeholder="https://..." value={form.justificatif_url} onChange={e => setField('justificatif_url', e.target.value)} />
                </div>
              </div>

              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg>{editId ? 'Mettre a jour' : 'Creer'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
